import type { ReactNode } from "react"
import { Autocomplete, Button, Stack, TextField } from "@mui/material"
import { infiniteListboxProps } from "@core/components/infiniteAutocomplete"
import { FeatureErrors } from "./featureErrors"

export interface ResourceSelectProps<T> {
  label: string
  value: T | null
  /** Accumulated loaded pages only, never an eagerly downloaded entire catalog. */
  options: readonly T[]
  /** Use a composite key for catalogs where same-ID/different-name options coexist. */
  getOptionKey: (value: T) => string
  getOptionLabel: (value: T) => string
  onChange: (value: T | null) => void
  onSearch: (search: string) => void
  onOpen: () => void
  onLoadMore: () => void
  hasMore: boolean
  isFetching: boolean
  disabled?: boolean
  size?: "small" | "medium"
  error?: unknown
  onRetry?: () => void
}

/** Controlled page adapter. The owner calls its RTK hooks unconditionally and
 * owns query/search/page state. No hidden fetching or automatic all-page loop.
 * A saved selection wins over refreshed catalog metadata with the same key.
 */
export function ResourceSelect<T>({
  label,
  value,
  options,
  getOptionKey,
  getOptionLabel,
  onChange,
  onSearch,
  onOpen,
  onLoadMore,
  hasMore,
  isFetching,
  disabled,
  size,
  error,
  onRetry,
}: ResourceSelectProps<T>) {
  const retained = new Map<string, T>()
  if (value !== null) retained.set(getOptionKey(value), value)
  for (const option of options)
    if (!retained.has(getOptionKey(option)))
      retained.set(getOptionKey(option), option)
  return (
    <Stack spacing={1}>
      <Autocomplete<T>
        size={size}
        value={value}
        options={[...retained.values()]}
        disabled={disabled}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={(option, selected) =>
          getOptionKey(option) === getOptionKey(selected)
        }
        filterOptions={(items) => items}
        onChange={(_event, selected) => onChange(selected)}
        onInputChange={(_event, input, reason) => {
          if (reason === "input" || reason === "clear") onSearch(input)
        }}
        onOpen={onOpen}
        {...infiniteListboxProps({ hasMore, isFetching, onLoadMore })}
        renderOption={(props, option, state) =>
          [
            { ...props, key: getOptionKey(option) },
            getOptionLabel(option),
            state,
          ] as ReactNode
        }
        noOptionsText="Нет вариантов"
        renderInput={(params) => <TextField {...params} label={label} />}
      />
      {hasMore && (
        <Button disabled={disabled || isFetching} onClick={onLoadMore}>
          Загрузить ещё
        </Button>
      )}
      {error != null && (
        <>
          <FeatureErrors error={error} />
          {onRetry && (
            <Button disabled={disabled || isFetching} onClick={onRetry}>
              Повторить загрузку
            </Button>
          )}
        </>
      )}
    </Stack>
  )
}
